// Crop Detail (web) — "The Mirror+". Top half is a 1:1 mirror of the mobile
// crop guide (hero + shared-element zoom, frosted Climate HUD, Jan–Dec sowing
// bar chart in lime/gold/gray, Vitality HUD). The right column adds the desktop
// "Biological Intelligence" layer (Visual Growth Timeline, Succession Strategist,
// Interactive Companion Map) and a bottom Strategic What/Why/Where/How briefing.
// Frosted glass, zero hard lines — depth from shadow + negative space.
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '@/components/Layout';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { VarietyPicker } from '@/components/VarietyPicker';
import {
  ArrowLeft, ArrowRight, Sun, Sprout, AlertTriangle, X, Thermometer, Flame, CalendarClock,
  MapPin, Bookmark, Home, Warehouse, ThumbsUp, ThumbsDown, Repeat, Scissors, Shovel, Lightbulb, Leaf,
  HelpCircle, ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { ZeroWasteRecipes, PriceCheckWidget } from '@/components/CropKitchen';
import { RegionalGuideCard } from '@/components/RegionalGuide';
import { useWeatherPredictions, WeatherForecastCard } from '@/components/WeatherForecast';
import { cropImage } from '@/lib/cropImage';
import { growthStages } from '@/lib/growthStages';
import { loadWithCache } from '@/lib/offlineCache';
import { OfflineBanner } from '@/components/OfflineBanner';
import { PremiumLockScreen } from '@/components/withPremiumGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE', sage: '#E5EBDD', sageDeep: '#D9E1CB',
  gold: '#C9A85B', goldTint: 'rgba(201,168,91,0.16)', lime: '#B4D330', leaf: '#6B9F4A', leafDeep: '#4E7A33', leafTint: 'rgba(107,159,74,0.16)',
  terracotta: '#C66B4A', terracottaTint: 'rgba(198,107,74,0.12)', sky: '#8FB4D6', gray: '#9CA6A0',
  text: '#2A3528', textMuted: '#5C665C', textSoft: '#7A6B57',
};
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_ABBR = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return ''; } };

function useRegionalGuide(cropId) {
  const [guide, setGuide] = useState(null);
  useEffect(() => {
    if (!cropId) return;
    loadWithCache(`crop_guide_${cropId}`, async () => (await axios.get(`${API}/crops/${cropId}/regional-guide`, { withCredentials: true })).data)
      .then(({ data }) => setGuide(data)).catch(() => {});
  }, [cropId]);
  return guide;
}

function SectionTag({ text, color }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="rounded-full" style={{ width: 16, height: 3, background: color }} />
      <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.18em', color }}>{text}</span>
    </div>
  );
}

/* ---------------- Core Sowing Guide — Jan–Dec bar chart (lime/gold/gray) ---------------- */
function SowingBarChart({ guide }) {
  const sow = new Set((guide?.sowing_months || []).map((m) => MONTHS_FULL.indexOf(m)));
  const tp = new Set((guide?.transplant_months || []).map((m) => MONTHS_FULL.indexOf(m)));
  const harv = new Set((guide?.harvest_months || []).map((m) => MONTHS_FULL.indexOf(m)));
  const nowIdx = MONTHS_FULL.indexOf(guide?.current_month);
  const rows = [
    { key: 'sow', label: 'Sow', set: sow, color: C.lime, icon: Sprout },
    { key: 'plant', label: 'Plant', set: tp, color: C.gold, icon: Shovel },
    { key: 'harvest', label: 'Harvest', set: harv, color: C.gray, icon: Scissors },
  ];
  return (
    <div className="hg-glass hg-float rounded-3xl p-6" data-testid="sowing-barchart">
      <SectionTag text="Core Sowing Guide" color={C.forest} />
      <p className="text-sm mb-4" style={{ color: C.textSoft }}>When to sow, plant out and harvest across the year for your area.</p>
      {/* Month header */}
      <div className="grid items-center gap-1" style={{ gridTemplateColumns: '64px repeat(12, 1fr)' }}>
        <div />
        {MONTHS_ABBR.map((m, i) => (
          <div key={i} className="text-center text-[11px] font-bold" style={{ color: i === nowIdx ? C.forest : C.textMuted }}>{m}</div>
        ))}
        {rows.map((row) => {
          const Icon = row.icon;
          return (
            <React.Fragment key={row.key}>
              <div className="flex items-center gap-1.5 py-1">
                <Icon className="w-3.5 h-3.5" style={{ color: row.color === C.gray ? C.textSoft : row.color }} />
                <span className="text-xs font-semibold" style={{ color: C.text }}>{row.label}</span>
              </div>
              {MONTHS_ABBR.map((_, i) => {
                const on = row.set.has(i);
                return (
                  <div key={i} className="flex items-center justify-center">
                    <motion.div
                      initial={{ scaleY: 0.3, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }}
                      transition={{ delay: i * 0.015, type: 'spring', stiffness: 260, damping: 22 }}
                      className="w-full rounded-md"
                      style={{
                        height: 26,
                        background: on ? row.color : 'rgba(26,67,49,0.06)',
                        boxShadow: on ? `0 6px 14px -8px ${row.color}` : 'none',
                        outline: i === nowIdx ? `2px solid ${C.forest}` : 'none',
                        outlineOffset: 1,
                      }}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 flex-wrap">
        {[['Sow', C.lime], ['Plant out', C.gold], ['Harvest', C.gray]].map(([l, col]) => (
          <span key={l} className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.textSoft }}>
            <span className="rounded-full" style={{ width: 12, height: 12, background: col }} /> {l}
          </span>
        ))}
        {nowIdx >= 0 && <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.forest }}><span className="rounded-md" style={{ width: 12, height: 12, outline: `2px solid ${C.forest}`, outlineOffset: -1 }} /> This month</span>}
      </div>
    </div>
  );
}

/* ---------------- Seed Depth Scale ---------------- */
function SeedDepthScale({ crop }) {
  const cm = crop?.seed_depth_cm;
  if (cm == null) return null;
  const label = crop.seed_depth_label || `Sow at ${cm}cm deep`;
  const planted = /^plant/i.test(label);
  const maxScale = cm <= 1 ? 2 : cm <= 2 ? 3 : cm <= 3 ? 5 : cm <= 5 ? 6 : cm <= 8 ? 10 : cm <= 10 ? 12 : 15;
  const SURF = 16, BOT = 150, usable = BOT - SURF;
  const markerY = SURF + (cm / maxScale) * usable;
  return (
    <div className="hg-glass hg-float rounded-3xl p-5" data-testid="seed-depth-scale">
      <SectionTag text="Seed Depth" color={C.leafDeep} />
      <div className="flex items-center gap-5">
        <div className="relative flex-shrink-0" style={{ width: 118, height: 160 }}>
          {/* soil column */}
          <div style={{ position: 'absolute', left: 30, top: SURF, bottom: 10, width: 18, borderRadius: 9, background: 'linear-gradient(to bottom,#CBA368,#9A6B3C 55%,#6E4725)' }} />
          {/* surface line + labels */}
          <div style={{ position: 'absolute', left: 8, width: 62, top: SURF, height: 2, background: C.leaf, opacity: 0.55 }} />
          <span style={{ position: 'absolute', left: 54, top: SURF - 14, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: C.textMuted }}>soil surface</span>
          <span style={{ position: 'absolute', left: 54, bottom: 0, fontSize: 10, fontWeight: 600, color: C.textSoft }}>{maxScale}cm</span>
          {/* seed marker */}
          <motion.div initial={{ opacity: 0, top: SURF }} animate={{ opacity: 1, top: markerY - 9 }} transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.1 }}
            className="flex items-center" style={{ position: 'absolute', left: 31 }}>
            <span style={{ width: 16, height: 19, background: '#5B3A1E', borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%', transform: 'rotate(14deg)', boxShadow: '0 2px 6px rgba(15,45,31,0.35)', border: '1.5px solid #3C2512' }} />
            <span style={{ height: 0, borderTop: `2px dashed ${C.gold}`, width: 26, marginLeft: 7 }} />
            <span style={{ marginLeft: 6, background: C.gold, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{cm}cm</span>
          </motion.div>
        </div>
        <div className="flex-1">
          <p className="text-3xl font-bold leading-none" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{cm}<span className="text-lg" style={{ color: C.textSoft }}> cm</span></p>
          <p className="text-sm font-semibold mt-1.5" style={{ color: C.leafDeep }}>{label}</p>
          <p className="text-[13px] mt-2 leading-relaxed" style={{ color: C.textSoft }}>
            {planted ? 'Set it in firmed soil and water in well.' : "Cover with fine soil and firm gently — roughly 2–3× the seed's width."}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- At a glance (blurb · yield · spacing · sowing) ---------------- */
function AtAGlance({ crop }) {
  if (!crop) return null;
  const rows = [
    crop.yield_guide && { label: 'Yield', body: crop.yield_guide },
    crop.spacing_guide && { label: 'Spacing', body: crop.spacing_guide },
    crop.seed_sowing_note && { label: 'Sowing', body: crop.seed_sowing_note },
  ].filter(Boolean);
  if (!crop.blurb && rows.length === 0) return null;
  return (
    <div className="hg-glass hg-float rounded-3xl p-5" data-testid="crop-at-a-glance">
      <SectionTag text="At a glance" color={C.leaf} />
      {crop.blurb && <p className="text-[15px] leading-relaxed mb-3.5" style={{ color: C.text }}>{crop.blurb}</p>}
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex gap-3">
            <span className="text-[11px] font-bold uppercase tracking-wide flex-shrink-0 mt-0.5" style={{ width: 62, color: C.leafDeep }}>{r.label}</span>
            <p className="text-sm leading-relaxed flex-1" style={{ color: C.textSoft }}>{r.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Vitality HUD ---------------- */
function VitalityHUD({ crop, guide, soil }) {
  const weeks = guide?.weeks_from_seed || guide?.weeks_to_maturity;
  const matDays = guide?.days_from_seed || crop.days_to_harvest;
  const matRange = guide?.days_from_seed_range || guide?.days_to_maturity_range;
  const fromSeed = (guide?.nursery_days || 0) > 0;
  const tiles = [
    { icon: Thermometer, color: C.leafDeep, label: 'Soil Temp', value: soil?.available ? `${soil.value}°C` : (crop.gdd_base_temp ? `≥ ${crop.gdd_base_temp}°C` : '—'), sub: soil?.available ? 'your soil now' : 'to germinate' },
    { icon: Flame, color: C.gold, label: 'Heat Units', value: crop.gdd_base_temp ? `${crop.gdd_base_temp}°C base` : '—', sub: 'GDD counts above this' },
    { icon: CalendarClock, color: C.forest, label: 'Days to Harvest', value: matRange ? `${matRange[0]}–${matRange[1]}` : (matDays ? `${matDays}` : '—'), sub: matRange && matDays ? `~${matDays} likely${fromSeed ? ' · from seed' : ''}` : (weeks ? `~${weeks} wks${fromSeed ? ' · from seed' : ''}` : 'days') },
  ];
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="vitality-hud">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div key={t.label} className="hg-glass hg-float rounded-2xl p-4 text-center">
            <Icon className="w-5 h-5 mx-auto" style={{ color: t.color }} />
            <p className="text-[10px] font-bold uppercase mt-2" style={{ letterSpacing: '0.12em', color: C.textMuted }}>{t.label}</p>
            <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{t.value}</p>
            <p className="text-[11px]" style={{ color: C.textSoft }}>{t.sub}</p>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Web-Plus: Visual Growth Timeline ---------------- */
function GrowthTimeline({ crop }) {
  const stages = useMemo(() => growthStages(crop.name, crop.days_to_harvest), [crop]);
  const [idx, setIdx] = useState(0);
  const s = stages[idx];
  return (
    <div className="hg-glass hg-float rounded-3xl p-5" data-testid="growth-timeline">
      <SectionTag text="Visual Growth Timeline" color={C.leaf} />
      <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '16 / 10', background: C.sageDeep }}>
        <AnimatePresence mode="wait">
          <motion.img key={s.img} src={s.img} alt={s.title}
            initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            className="absolute inset-0 w-full h-full object-cover" />
        </AnimatePresence>
        <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: 'linear-gradient(to top, rgba(15,45,31,0.9), transparent)' }} />
        <div className="absolute bottom-3 left-4 right-4">
          <span className="inline-block rounded-full px-2.5 py-1 text-[11px] font-bold mb-1" style={{ background: C.lime, color: C.forest }}>WEEK {s.week}</span>
          <p className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream }}>{s.title}</p>
        </div>
      </div>
      {/* Scrubber */}
      <div className="flex items-center gap-2 mt-4">
        {stages.map((st, i) => (
          <button key={i} onClick={() => setIdx(i)} data-testid={`growth-stage-${i}`}
            className="flex-1 rounded-full transition-all hg-press" style={{ height: 6, background: i <= idx ? C.leaf : 'rgba(26,67,49,0.12)' }} aria-label={`Week ${st.week}`} />
        ))}
      </div>
      <div className="flex items-center justify-between mt-2">
        {stages.map((st, i) => (
          <button key={i} onClick={() => setIdx(i)} className="text-[11px] font-semibold" style={{ color: i === idx ? C.forest : C.textMuted }}>Wk {st.week}</button>
        ))}
      </div>
      <p className="text-sm mt-3 leading-relaxed" style={{ color: C.text }}>{s.note}</p>
    </div>
  );
}

/* ---------------- Web-Plus: Succession Sowing Strategist ---------------- */
function SuccessionStrategist({ succession, guide, crop }) {
  const suitable = !!succession?.succession_suitable;
  const open = suitable && succession?.window_open;
  return (
    <div className="hg-glass hg-float rounded-3xl p-5" data-testid="succession-strategist">
      <SectionTag text="Succession Strategist" color={C.gold} />
      {open ? (
        <>
          <p className="text-sm" style={{ color: C.text }}>
            Sow <span className="font-bold" style={{ color: C.forest }}>{succession.batch_size_hint}</span> every{' '}
            <span className="font-bold" style={{ color: C.forest }}>{succession.interval_weeks} weeks</span> for a steady harvest — never a glut.
          </p>
          <div className="flex gap-2 overflow-x-auto hg-scroll mt-4 pb-1">
            {succession.schedule.slice(0, 6).map((b) => (
              <div key={b.batch} className="flex-shrink-0 rounded-2xl p-3 text-center" style={{ width: 92, background: b.status === 'now' ? C.forest : C.creamWarm, boxShadow: '0 10px 24px -16px rgba(26,67,49,0.5)' }}>
                <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: b.status === 'now' ? C.lime : C.textMuted }}>{b.status === 'now' ? 'Sow now' : `Batch ${b.batch}`}</p>
                <p className="text-sm font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: b.status === 'now' ? C.cream : C.forest }}>{fmtShort(b.date)}</p>
                <div className="flex items-center justify-center gap-1 mt-1" style={{ color: b.status === 'now' ? 'rgba(250,246,236,0.7)' : C.textSoft }}>
                  <Scissors className="w-3 h-3" /><span className="text-[10px]">{fmtShort(b.harvest_date)}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs mt-3 leading-relaxed" style={{ color: C.textSoft }}>{succession.stop_reason}</p>
        </>
      ) : suitable ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="w-4 h-4" style={{ color: C.gold }} />
            <span className="font-semibold" style={{ color: C.forest }}>Stagger for a steady supply</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: C.text }}>{succession.why || `Sow ${succession.batch_size_hint} every ${succession.interval_weeks} weeks rather than all at once for a continuous harvest.`}</p>
          <div className="flex items-center gap-2 mt-3 rounded-2xl p-3" style={{ background: C.goldTint }}>
            <CalendarClock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8A6D2B' }} />
            <span className="text-xs font-semibold" style={{ color: '#8A6D2B' }}>{succession.stop_reason || `Succession sowing starts when the window opens${guide?.next_sow_month ? ` (${guide.next_sow_month})` : ''}.`}</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="w-4 h-4" style={{ color: C.gold }} />
            <span className="font-semibold" style={{ color: C.forest }}>A plant-once crop</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: C.text }}>
            {crop.name} is best planted in a single main sowing rather than staggered batches.
            {guide?.sowing_range ? ` Sow within its window (${guide.sowing_range}) once the frost risk has passed.` : ''}
          </p>
          {guide?.harvest_range && (
            <div className="flex items-center gap-2 mt-3 rounded-2xl p-3" style={{ background: C.creamWarm }}>
              <span className="text-xs font-semibold" style={{ color: C.textSoft }}>Sow {guide.sowing_range}</span>
              <ArrowRight className="w-3.5 h-3.5" style={{ color: C.gold }} />
              <span className="text-xs font-semibold" style={{ color: C.forest }}>Harvest {guide.harvest_range}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- Web-Plus: Interactive Companion Map ---------------- */
function CompanionRow({ c, foe, idx }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const accent = foe ? C.terracotta : C.leafDeep;
  const tint = foe ? C.terracottaTint : C.leafTint;
  const linkable = !!c.crop_id;
  return (
    <div className="rounded-2xl px-3.5 py-2.5" style={{ background: tint }} data-testid={`companion-${foe ? 'foe' : 'friend'}-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        {linkable ? (
          <button onClick={() => navigate(`/crops/${c.crop_id}`)} data-testid={`companion-link-${foe ? 'foe' : 'friend'}-${idx}`}
            className="hg-press inline-flex items-center gap-1 text-sm font-bold" style={{ color: C.forest }}>
            {c.name} <ArrowRight className="w-3.5 h-3.5" style={{ color: accent }} />
          </button>
        ) : (
          <span className="text-sm font-bold" style={{ color: C.forest }}>{c.name}</span>
        )}
        <button onClick={() => setOpen((o) => !o)} data-testid={`companion-why-${foe ? 'foe' : 'friend'}-${idx}`} aria-expanded={open}
          className="hg-press inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{ background: open ? accent : 'rgba(255,255,255,0.65)', color: open ? C.cream : accent }}>
          <HelpCircle className="w-3 h-3" /> Why <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="why" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
            <p className="text-[13px] leading-snug mt-2" style={{ color: C.text }} data-testid={`companion-reason-${foe ? 'foe' : 'friend'}-${idx}`}>{c.why}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CompanionMap({ companion, crop }) {
  const friends = (companion?.friends?.length ? companion.friends : (crop.companion_plants || []).map((n) => ({ name: n, why: '' }))).slice(0, 6);
  const foes = (companion?.foes?.length ? companion.foes : (crop.bad_companions || []).map((n) => ({ name: n, why: '' }))).slice(0, 4);
  if (!friends.length && !foes.length) return null;
  return (
    <div className="hg-glass hg-float rounded-3xl p-5" data-testid="companion-map">
      <SectionTag text="Companion Map" color={C.leafDeep} />
      <p className="flex items-center gap-1.5 text-xs font-bold mb-2" style={{ color: C.leafDeep }}><ThumbsUp className="w-3.5 h-3.5" /> Best friends</p>
      <div className="space-y-2">
        {friends.map((f, i) => <CompanionRow key={f.name} c={f} foe={false} idx={i} />)}
      </div>
      <p className="flex items-center gap-1.5 text-xs font-bold mb-2 mt-4" style={{ color: C.terracotta }}><ThumbsDown className="w-3.5 h-3.5" /> Keep apart</p>
      {foes.length ? (
        <div className="space-y-2">
          {foes.map((f, i) => <CompanionRow key={f.name} c={f} foe idx={i} />)}
        </div>
      ) : <p className="text-xs" style={{ color: C.textSoft }}>No known foes — plays well with most beds.</p>}
    </div>
  );
}

/* ---------------- Bottom: Strategic What/Why/Where/How briefing ---------------- */
function StrategyBriefing({ crop, guide, beds }) {
  const phases = [
    guide?.sowing_range && guide.sowing_range !== '—' ? `Sow ${guide.sowing_range}` : null,
    guide?.transplant_range && guide.transplant_range !== '—' ? `plant out ${guide.transplant_range}` : null,
    guide?.harvest_range && guide.harvest_range !== '—' ? `harvest ${guide.harvest_range}` : null,
  ].filter(Boolean).join(' → ');
  const cells = [
    { tag: 'WHAT', color: C.forest, body: `${crop.name} — a great pick for your garden.${crop.description ? ' ' + crop.description : ''}` },
    { tag: 'WHY', color: '#3F6C97', body: guide?.plain_summary || `${crop.name} rewards a warm, sunny spot and steady water with a reliable home harvest.` },
    { tag: 'WHERE', color: C.gold, body: `${beds.map((b) => b.label).join(', ')}. ${crop.sun_requirements ? `Needs ${crop.sun_requirements.toLowerCase()}.` : ''}` },
    { tag: 'HOW', color: C.leafDeep, body: phases || `Sow, grow on in warm soil, then harvest in about ${guide?.weeks_from_seed || guide?.weeks_to_maturity || Math.round((crop.days_to_harvest || 84) / 7)} weeks.` },
  ];
  return (
    <div className="hg-glass hg-float rounded-3xl p-6" data-testid="strategy-briefing">
      <div className="flex items-center gap-2 mb-4">
        <Leaf className="w-5 h-5" style={{ color: C.leaf }} />
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Master-gardener strategy</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {cells.map((c) => (
          <div key={c.tag}>
            <SectionTag text={c.tag} color={c.color} />
            <p className="text-sm leading-relaxed" style={{ color: C.text }}>{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CropDetail({ user }) {
  const { cropId } = useParams();
  const navigate = useNavigate();
  const [crop, setCrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [zoomBack, setZoomBack] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [companion, setCompanion] = useState(null);
  const [succession, setSuccession] = useState(null);
  const [saved, setSaved] = useState(false);
  const [offline, setOffline] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [member, setMember] = useState(true);
  const [freeSet, setFreeSet] = useState(null);

  const guide = useRegionalGuide(cropId);
  const weather = useWeatherPredictions();
  const weatherReport = (weather?.predictions || []).find((r) => r.crop_id === cropId);

  const zoomOrigin = useMemo(() => {
    try { const o = JSON.parse(sessionStorage.getItem('hg_zoom_origin') || 'null'); return o && o.id === cropId ? o : null; } catch { return null; }
  }, [cropId]);
  const zoomOutToLibrary = (e) => {
    if (e) e.preventDefault();
    const heroEl = document.querySelector('[data-testid="crop-detail-image"]');
    if (!heroEl || !zoomOrigin) { navigate('/crops'); return; }
    const r = heroEl.getBoundingClientRect();
    setZoomBack({ img: heroEl.getAttribute('src'), from: { top: r.top, left: r.left, width: r.width, height: r.height }, to: zoomOrigin.rect });
  };

  const [formData, setFormData] = useState({ sowing_date: new Date().toISOString().split('T')[0], variety: '', plant_count: '', notes: '', environment: 'outdoor', custom_days: '' });
  const [matchingSeeds, setMatchingSeeds] = useState([]);
  const [seedChoice, setSeedChoice] = useState({ seed_id: '', seeds_used: '' });
  const [varieties, setVarieties] = useState([]);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('add') === '1') {
      const v = searchParams.get('variety');
      if (v) setFormData((f) => ({ ...f, variety: v }));
      setShowAddModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadCrop = useCallback(() => {
    setLoading(true);
    loadWithCache(`crop_${cropId}`, async () => (await axios.get(`${API}/crops/${cropId}`)).data)
      .then(({ data, offline: off, savedAt: sa }) => { setCrop(data); setOffline(off); setSavedAt(sa); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cropId]);

  useEffect(() => {
    loadCrop();
    axios.get(`${API}/ai/coach-vitals`, { withCredentials: true }).then((r) => setVitals(r.data)).catch(() => {});
    axios.get(`${API}/crops/${cropId}/companions`, { withCredentials: true }).then((r) => setCompanion(r.data)).catch(() => setCompanion(null));
    axios.get(`${API}/succession/${cropId}`, { withCredentials: true }).then((r) => setSuccession(r.data)).catch(() => setSuccession(null));
    axios.get(`${API}/crops/shortlist`, { withCredentials: true }).then((r) => setSaved((r.data.crop_ids || []).includes(cropId))).catch(() => {});
    axios.get(`${API}/entitlements`, { withCredentials: true }).then((r) => { setMember(!!r.data?.member); setFreeSet(new Set(r.data?.handbook?.free_crop_ids || [])); }).catch(() => {});
  }, [cropId, loadCrop]);

  useEffect(() => {
    const onOnline = () => loadCrop();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [loadCrop]);

  useEffect(() => {
    if (!showAddModal || !crop) return;
    axios.get(`${API}/seeds`, { withCredentials: true }).then((res) => {
      const name = (crop.name || '').toLowerCase().trim();
      setMatchingSeeds((res.data.seeds || []).filter((s) => (s.crop_id && crop.crop_id && s.crop_id === crop.crop_id) || (s.crop_name || '').toLowerCase().trim() === name));
    }).catch(() => {});
  }, [showAddModal, crop]);

  useEffect(() => {
    if (!crop?.name) return;
    axios.get(`${API}/varieties/for-crop/${encodeURIComponent(crop.name)}`, { withCredentials: true })
      .then((r) => setVarieties(r.data.varieties || []))
      .catch(() => setVarieties([]));
  }, [crop]);

  const toggleSave = async () => {
    const next = !saved; setSaved(next);
    try {
      if (next) await axios.post(`${API}/crops/shortlist`, { crop_id: cropId, crop_name: crop?.name, category: crop?.category }, { withCredentials: true });
      else await axios.delete(`${API}/crops/shortlist/${cropId}`, { withCredentials: true });
    } catch { setSaved(!next); }
  };

  const handleAddToGarden = async (e) => {
    e.preventDefault(); setAdding(true);
    try {
      await axios.post(`${API}/plantings`, {
        crop_id: crop.crop_id, crop_name: crop.name, variety: formData.variety || null,
        variety_id: selectedVariety?.variety_id || null,
        days_to_maturity: selectedVariety?.days_to_maturity || (formData.custom_days ? parseInt(formData.custom_days) : null),
        sowing_date: new Date(formData.sowing_date).toISOString(),
        days_to_harvest: selectedVariety?.days_to_maturity || (formData.custom_days ? parseInt(formData.custom_days) : null) || crop.days_to_harvest,
        plant_count: formData.plant_count ? parseInt(formData.plant_count) : null, notes: formData.notes || null,
        stage: 'seed', environment: formData.environment,
        seed_id: seedChoice.seed_id || null, seeds_used: (seedChoice.seed_id && seedChoice.seeds_used) ? parseInt(seedChoice.seeds_used) : null,
      }, { withCredentials: true });
      setShowAddModal(false); setSeedChoice({ seed_id: '', seeds_used: '' }); navigate('/my-crops');
    } catch { /* ignore */ } finally { setAdding(false); }
  };

  if (loading) {
    return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div></Layout>;
  }
  if (!crop) {
    return <Layout user={user}><div className="text-center py-12"><p style={{ color: C.textMuted }}>Crop not found</p><Link to="/crops" className="font-semibold mt-4 inline-block" style={{ color: C.forest }}>Back to library</Link></div></Layout>;
  }
  if (!member && freeSet && !freeSet.has(cropId)) {
    return <PremiumLockScreen user={user} title={`${crop.name} · Premium`} blurb={`This crop guide is part of Homegrown Premium. Free growers can explore the first ${freeSet.size} crops in the Handbook — unlock the full library any time.`} />;
  }

  const soil = vitals?.soil_temp;
  const sowable = guide?.currently_sowable || guide?.currently_transplantable;
  const ft = (crop.frost_tolerance || '').toLowerCase();
  const frostSensitive = /sensitive|tender/.test(ft) || (ft.includes('frost') && !/(hardy|tolerant)/.test(ft));
  const beds = [{ icon: Sun, label: 'Outdoor beds', note: crop.sun_requirements || 'Open ground', rec: true }];
  if (frostSensitive) beds.push({ icon: Warehouse, label: 'Greenhouse / under cover', note: 'Best for an early, frost-safe start', rec: true });
  if (crop.container_suitable) beds.push({ icon: Home, label: 'Containers', note: 'Patio & balcony friendly', rec: false });

  const daysLabel = (guide?.weeks_from_seed || guide?.weeks_to_maturity) ? `~${guide.weeks_from_seed || guide.weeks_to_maturity} wks` : crop.days_to_harvest ? `${crop.days_to_harvest} days` : '—';
  const region = guide?.location || guide?.zone_region || user?.location;

  return (
    <Layout user={user}>
      <div className="space-y-6 pb-6">
        <Link to="/crops" data-testid="back-to-crops" onClick={zoomOutToLibrary} className="inline-flex items-center gap-2 transition-colors" style={{ color: C.textMuted }}>
          <ArrowLeft className="w-4 h-4" /> <span className="font-medium">Back to library</span>
        </Link>

        {offline && <OfflineBanner savedAt={savedAt} onRetry={loadCrop} />}

        {zoomBack && createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 120 }} data-testid="guide-zoom-back">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.28 }} style={{ position: 'absolute', inset: 0, background: 'rgba(15,45,31,0.42)', backdropFilter: 'blur(3px)' }} />
            <motion.img src={zoomBack.img} alt="" style={{ position: 'fixed', objectFit: 'cover', boxShadow: '0 40px 90px -24px rgba(15,45,31,0.5)', zIndex: 121 }}
              initial={{ top: zoomBack.from.top, left: zoomBack.from.left, width: zoomBack.from.width, height: zoomBack.from.height, borderRadius: 24 }}
              animate={{ top: zoomBack.to.top, left: zoomBack.to.left, width: zoomBack.to.width, height: zoomBack.to.height, borderRadius: 20 }}
              transition={{ duration: 0.44, ease: [0.22, 0.61, 0.36, 1] }} onAnimationComplete={() => navigate('/crops')} />
          </div>, document.body)}

        {/* Hero — 1:1 with mobile */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative rounded-[28px] overflow-hidden" style={{ height: 320 }} data-testid="crop-hero">
          <img src={cropImage(crop.name, crop.category, crop.image_url)} alt={crop.name} className="absolute inset-0 w-full h-full object-cover" data-testid="crop-detail-image" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(15,45,31,0.32), rgba(15,45,31,0.18) 40%, rgba(15,45,31,0.94))' }} />
          <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
            <button onClick={zoomOutToLibrary} data-testid="crop-back" className="w-10 h-10 rounded-full flex items-center justify-center hg-press" style={{ background: 'rgba(250,246,236,0.92)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.4)' }}><ArrowLeft className="w-5 h-5" style={{ color: C.forest }} /></button>
            <div className="flex items-center gap-2">
              <button onClick={toggleSave} data-testid="guide-bookmark" className="w-10 h-10 rounded-full flex items-center justify-center hg-press" style={{ background: 'rgba(250,246,236,0.92)', boxShadow: '0 6px 16px -8px rgba(26,67,49,0.4)' }}><Bookmark className="w-5 h-5" style={{ color: saved ? C.gold : C.forest, fill: saved ? C.gold : 'none' }} /></button>
              <span className="rounded-full px-3 py-1.5 text-xs font-semibold capitalize" style={{ background: 'rgba(250,246,236,0.92)', color: C.forest }}>{crop.difficulty}</span>
            </div>
          </div>
          <div className="absolute left-6 right-6 bottom-6">
            <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.2em', color: C.lime }}>Regional Crop Handbook</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: C.cream }}>{crop.name}</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(250,246,236,0.85)' }}>{crop.category}{crop.family ? ` · ${crop.family}` : ''}</p>
          </div>
        </motion.div>

        {/* Climate HUD */}
        {region && (
          <div className="hg-glass hg-float rounded-3xl p-5" data-testid="climate-hud">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" style={{ color: C.leafDeep }} />
                <div>
                  <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.14em', color: C.textMuted }}>Your climate</p>
                  <p className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{region}</p>
                </div>
                <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: C.leafTint, color: C.leafDeep }}>Your area</span>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.text }}><Thermometer className="w-4 h-4" style={{ color: C.leafDeep }} />{soil?.available ? `${soil.value}°C soil` : 'Soil —'}</span>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.text }}><CalendarClock className="w-4 h-4" style={{ color: C.leafDeep }} />{daysLabel} to harvest</span>
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ background: sowable ? C.leafTint : C.goldTint, color: sowable ? C.leafDeep : '#8A6D2B' }}>
                  <span className="rounded-full" style={{ width: 7, height: 7, background: sowable ? C.leaf : C.gold }} /> {sowable ? 'Sow now' : `Sow ${guide?.next_sow_month || 'soon'}`}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Plant this now */}
        <button onClick={() => setShowAddModal(true)} data-testid="add-to-garden-button" className="w-full flex items-center justify-center gap-2 rounded-full py-4 font-semibold hg-press" style={{ background: C.forest, color: C.cream, boxShadow: '0 14px 32px -12px rgba(26,67,49,0.55)' }}>
          <Sprout className="w-5 h-5" /> Plant this now <ArrowRight className="w-4 h-4" style={{ color: C.lime }} />
        </button>

        {varieties.length > 0 && (
          <VarietyPicker varieties={varieties} selected={selectedVariety?.variety_id || null} onSelect={(v) => { setSelectedVariety(v); if (v) setFormData((f) => ({ ...f, variety: v.variety_name })); }} />
        )}

        {/* Mirror (left) + Web-Plus Biological Intelligence (right) */}
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
            <AtAGlance crop={crop} />
            <SowingBarChart guide={guide} />
            <SeedDepthScale crop={crop} />
            <VitalityHUD crop={crop} guide={guide} soil={soil} />
            {/* WHERE */}
            <div className="hg-glass hg-float rounded-3xl p-6">
              <SectionTag text="Where to grow" color={C.gold} />
              <div className="space-y-2.5">
                {beds.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.label} className="flex items-center gap-3 rounded-2xl p-3.5" style={{ background: b.rec ? C.creamWarm : 'rgba(255,255,255,0.5)' }}>
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: b.rec ? C.forest : 'rgba(26,67,49,0.06)' }}><Icon className="w-5 h-5" style={{ color: b.rec ? C.lime : C.forest }} /></span>
                      <div className="flex-1">
                        <p className="font-semibold" style={{ color: C.forest }}>{b.label}</p>
                        <p className="text-sm" style={{ color: C.textSoft }}>{b.note}</p>
                      </div>
                      {b.rec && <span className="rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: C.leafTint, color: C.leafDeep }}>IDEAL</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-5">
            <div className="flex items-center gap-2 px-1">
              <span className="rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: C.forest, color: C.lime, letterSpacing: '0.1em' }}>PREMIUM</span>
              <span className="text-sm font-bold" style={{ color: C.forest }}>Biological Intelligence</span>
            </div>
            <GrowthTimeline crop={crop} />
            <SuccessionStrategist succession={succession} guide={guide} crop={crop} />
            <CompanionMap companion={companion} crop={crop} />
          </div>
        </div>

        {/* Strategic briefing HUD */}
        <StrategyBriefing crop={crop} guide={guide} beds={beds} />

        {weatherReport && <WeatherForecastCard report={weatherReport} />}
        <RegionalGuideCard cropId={cropId} />

        {crop.common_pests && crop.common_pests.length > 0 && (
          <div className="hg-glass hg-float rounded-3xl p-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}><AlertTriangle className="w-5 h-5" style={{ color: C.terracotta }} /> Watch for</h2>
            <div className="flex flex-wrap gap-2">
              {crop.common_pests.map((pest, i) => <span key={i} className="px-3.5 py-1.5 rounded-full text-sm font-medium" style={{ background: C.goldTint, color: '#7A5E1F' }}>{pest}</span>)}
              {(crop.common_diseases || []).map((d, i) => <span key={`d-${i}`} className="px-3.5 py-1.5 rounded-full text-sm font-medium" style={{ background: C.terracottaTint, color: C.terracotta }}>{d}</span>)}
            </div>
          </div>
        )}

        {crop.tips && crop.tips.length > 0 && (
          <div className="hg-glass hg-float rounded-3xl p-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}><Lightbulb className="w-5 h-5" style={{ color: C.gold }} /> Grower tips</h2>
            <ul className="space-y-2.5">
              {crop.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: C.leaf }} /><span className="leading-relaxed" style={{ color: C.text }}>{tip}</span></li>
              ))}
            </ul>
          </div>
        )}

        <PriceCheckWidget cropName={crop.name} />
        <ZeroWasteRecipes cropName={crop.name} />
        <VarietyReviewsSection cropId={cropId} cropName={crop.name} user={user} />
        <p className="text-[11px] leading-relaxed px-2 pb-2" style={{ color: C.textMuted }} data-testid="planting-disclaimer">
          HomeGrown's planting and harvesting recommendations are guides based on available horticultural information and regional growing conditions. Actual results vary with weather, soil, crop variety and garden management. While we strive for accuracy, successful growth or harvest cannot be guaranteed. Nothing in this guidance limits rights under applicable New Zealand law.
        </p>
      </div>

      {/* Add to Garden Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-[80]" onClick={() => setShowAddModal(false)}>
          <div className="bg-[#FAF6EC] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-crop-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Add {crop.name}</h2>
              <button onClick={() => setShowAddModal(false)} data-testid="close-modal-button"><X className="w-5 h-5" style={{ color: C.textMuted }} /></button>
            </div>
            <form onSubmit={handleAddToGarden} className="space-y-4">
              {guide && (
                <div className={`rounded-2xl px-4 py-3 text-sm ${sowable ? 'bg-[#6FA22E]/10' : 'bg-[#F0F2EB]'}`} data-testid="modal-sow-hint">
                  <p className="font-semibold" style={{ color: C.forest }}>{sowable ? `✓ Good time to start in your area (${guide.current_month})` : `Best window: ${guide.sowing_range}`}</p>
                  <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{sowable ? `Ready to harvest in about ${guide.weeks_from_seed || guide.weeks_to_maturity} weeks.` : `${guide.next_sow_month ? `Next window: ${guide.next_sow_month}. ` : ''}Ready in ~${guide.weeks_from_seed || guide.weeks_to_maturity} weeks once sown.`}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Where are you growing it?</label>
                <div className="flex flex-wrap gap-2" data-testid="env-pills">
                  {[{ key: 'outdoor', label: 'Your garden' }, { key: 'greenhouse', label: 'Greenhouse' }, { key: 'indoor', label: 'Indoors' }].map((e) => (
                    <button type="button" key={e.key} onClick={() => setFormData({ ...formData, environment: e.key })} data-testid={`env-${e.key}`}
                      className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors" style={formData.environment === e.key ? { background: C.forest, color: C.cream, borderColor: C.forest } : { background: '#fff', color: C.forest, borderColor: 'rgba(26,67,49,0.15)' }}>{e.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Sowing Date</label>
                <input type="date" value={formData.sowing_date} onChange={(e) => setFormData({ ...formData, sowing_date: e.target.value })} required data-testid="sowing-date-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              {varieties.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Variety</label>
                  <select value={selectedVariety?.variety_id || ''} data-testid="variety-select"
                    onChange={(e) => { const v = varieties.find((x) => x.variety_id === e.target.value) || null; setSelectedVariety(v); setFormData((f) => ({ ...f, variety: v ? v.variety_name : f.variety })); }}
                    className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none">
                    <option value="">Choose a variety (sets harvest timing)…</option>
                    {varieties.map((v) => <option key={v.variety_id} value={v.variety_id}>{v.variety_name} · {v.maturity_label} · {v.harvest_class_label}</option>)}
                  </select>
                  {selectedVariety && <p className="text-xs mt-1.5" style={{ color: C.leafDeep }} data-testid="variety-select-note">✓ Harvest timing set to {selectedVariety.days_to_maturity} days ({selectedVariety.variety_name})</p>}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Variety name {varieties.length > 0 ? '(or type your own)' : '(optional)'}</label>
                <input type="text" value={formData.variety} onChange={(e) => setFormData({ ...formData, variety: e.target.value })} placeholder="e.g., Sweet 100" data-testid="variety-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              {!selectedVariety && (
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Days to maturity (optional)</label>
                  <input type="number" value={formData.custom_days} onChange={(e) => setFormData({ ...formData, custom_days: e.target.value })} placeholder={`e.g., ${crop.days_to_harvest || 70}`} data-testid="variety-days-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
                  <p className="text-xs mt-1.5" style={{ color: C.textSoft }}>Sets your harvest prediction for this variety.</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Plant Count (optional)</label>
                <input type="number" value={formData.plant_count} onChange={(e) => setFormData({ ...formData, plant_count: e.target.value })} placeholder="e.g., 6" data-testid="plant-count-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              {matchingSeeds.length > 0 && (
                <div className="bg-[#F0F2EB] rounded-2xl p-4 space-y-3" data-testid="sow-from-seed-block">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Sow from a seed packet (optional)</label>
                    <select value={seedChoice.seed_id} onChange={(e) => setSeedChoice({ ...seedChoice, seed_id: e.target.value })} data-testid="sow-from-seed-select" className="w-full px-4 py-3 bg-white rounded-xl outline-none">
                      <option value="">Don&apos;t track — just add to garden</option>
                      {matchingSeeds.map((s) => <option key={s.seed_id} value={s.seed_id}>{s.crop_name}{s.variety ? ` · ${s.variety}` : ''}{s.remaining_seeds != null ? ` (${s.remaining_seeds} left)` : ''}</option>)}
                    </select>
                  </div>
                  {seedChoice.seed_id && (
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Seeds used</label>
                      <input type="number" min="1" value={seedChoice.seeds_used} onChange={(e) => setSeedChoice({ ...seedChoice, seeds_used: e.target.value })} placeholder="e.g., 12" data-testid="sow-from-seed-used-input" className="w-full px-4 py-3 bg-white rounded-xl outline-none" />
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: C.text }}>Notes (optional)</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="notes-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none resize-none" />
              </div>
              <button type="submit" disabled={adding} data-testid="confirm-add-crop-button" className="w-full text-white font-semibold py-3 px-8 rounded-full disabled:opacity-50" style={{ background: C.forest }}>{adding ? 'Adding...' : 'Add to Garden'}</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default CropDetail;

const Stars = ({ rating, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => onChange && onChange(n)} className={`text-lg ${n <= rating ? 'text-[#C9A85B]' : 'text-[#E5EBDD]'}`}>★</button>
    ))}
  </div>
);

function VarietyReviewsSection({ cropId, cropName, user }) {
  const [reviews, setReviews] = React.useState([]);
  const [community, setCommunity] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ variety_name: '', yield_rating: 4, taste_rating: 4, ease_rating: 4, would_grow_again: true, notes: '' });
  const [submitting, setSubmitting] = React.useState(false);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/community/variety-reviews/${cropId}`, { withCredentials: true }),
        axios.get(`${API}/community/crop/${cropId}/community`, { withCredentials: true }),
      ]);
      setReviews(r.data); setCommunity(c.data);
    } catch { /* silent */ }
  }, [cropId]);
  React.useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await axios.post(`${API}/community/variety-reviews`, { ...form, crop_id: cropId }, { withCredentials: true });
      setShowAdd(false); setForm({ variety_name: '', yield_rating: 4, taste_rating: 4, ease_rating: 4, would_grow_again: true, notes: '' }); load();
    } catch { /* ignore */ } finally { setSubmitting(false); }
  };

  return (
    <div className="hg-glass hg-float rounded-3xl p-6" data-testid="variety-reviews-section">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Community varieties</h3>
          {community && community.total_posts > 0 && (
            <p className="text-xs mt-1" style={{ color: C.textSoft }}>{community.total_posts} {community.total_posts === 1 ? 'gardener has' : 'gardeners have'} shared {cropName} harvests. Average value: ${community.avg_value_low}–${community.avg_value_high}.</p>
          )}
        </div>
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-review-button" className="text-sm font-semibold border-2 py-2 px-4 rounded-full" style={{ color: C.forest, borderColor: C.forest }}>{showAdd ? 'Cancel' : 'Review a variety'}</button>
      </div>

      {community && community.popular_varieties && community.popular_varieties.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {community.popular_varieties.map((v) => <span key={v.variety} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: C.leafTint, color: C.forest }}>{v.variety} · {v.count}× grown</span>)}
        </div>
      )}

      {showAdd && (
        <form onSubmit={submit} className="bg-white/60 rounded-2xl p-4 mb-4 space-y-3" data-testid="variety-review-form">
          <input required placeholder="Variety name (e.g. Sweet 100)" value={form.variety_name} onChange={(e) => setForm({ ...form, variety_name: e.target.value })} data-testid="variety-name-input" className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none" />
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><p className="font-semibold mb-1" style={{ color: C.forest }}>Yield</p><Stars rating={form.yield_rating} onChange={(n) => setForm({ ...form, yield_rating: n })} testId="review-yield" /></div>
            <div><p className="font-semibold mb-1" style={{ color: C.forest }}>Taste</p><Stars rating={form.taste_rating} onChange={(n) => setForm({ ...form, taste_rating: n })} testId="review-taste" /></div>
            <div><p className="font-semibold mb-1" style={{ color: C.forest }}>Easy</p><Stars rating={form.ease_rating} onChange={(n) => setForm({ ...form, ease_rating: n })} testId="review-ease" /></div>
          </div>
          <textarea placeholder="A quick note (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.would_grow_again} onChange={(e) => setForm({ ...form, would_grow_again: e.target.checked })} /><span style={{ color: C.forest }}>Would grow again</span></label>
          <button type="submit" disabled={submitting} data-testid="submit-review-button" className="w-full text-[#FAF6EC] py-3 rounded-full font-semibold disabled:opacity-50" style={{ background: C.forest }}>{submitting ? 'Saving...' : 'Submit review'}</button>
        </form>
      )}

      {reviews.length === 0 ? (
        <p className="text-sm italic" style={{ color: C.textSoft }}>No variety reviews yet. Be the first to share.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white/40 rounded-2xl p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2"><p className="font-semibold" style={{ color: C.forest }}>{r.variety_name}</p><span className="text-xs" style={{ color: C.textSoft }}>{r.region} · {r.growing_style || 'mixed'}</span></div>
              <div className="flex gap-4 text-xs mt-2" style={{ color: C.textSoft }}>
                {r.yield_rating && <span>Yield: <span className="font-bold" style={{ color: C.gold }}>{'★'.repeat(r.yield_rating)}</span></span>}
                {r.taste_rating && <span>Taste: <span className="font-bold" style={{ color: C.gold }}>{'★'.repeat(r.taste_rating)}</span></span>}
                {r.ease_rating && <span>Easy: <span className="font-bold" style={{ color: C.gold }}>{'★'.repeat(r.ease_rating)}</span></span>}
              </div>
              {r.notes && <p className="text-sm mt-2 italic" style={{ color: C.text }}>&quot;{r.notes}&quot;</p>}
              {r.would_grow_again && <p className="text-xs font-semibold mt-2" style={{ color: C.leaf }}>Would grow again</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecommendedVarietiesSection({ cropName, user }) {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/varieties/for-crop/${cropName}`, { withCredentials: true }).then((res) => setVarieties(res.data.varieties || [])).catch(() => {}).finally(() => setLoading(false));
  }, [cropName]);
  if (loading || varieties.length === 0) return null;
  return (
    <div className="hg-glass hg-float rounded-3xl p-6" data-testid="recommended-varieties-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}><Sprout className="w-6 h-6" style={{ color: C.leaf }} /> Recommended Varieties</h2>
        <Link to="/varieties" className="text-sm font-semibold" style={{ color: C.forest }}>See all →</Link>
      </div>
      <p className="text-sm mb-4" style={{ color: C.textMuted }}>Homegrown-recommended {cropName.toLowerCase()} varieties matched to your garden</p>
      <div className="grid md:grid-cols-2 gap-4">
        {varieties.slice(0, 4).map((v) => (
          <div key={v.variety_id} className="bg-white/60 rounded-2xl p-4" data-testid={`variety-${v.variety_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1"><h3 className="font-bold" style={{ color: C.forest }}>{v.variety_name}</h3>{v.match_score >= 30 && <span className="text-xs font-semibold" style={{ color: C.leaf }}>{v.match_score >= 50 ? '✓ Perfect for you' : '✓ Good fit'}</span>}</div>
              {v.homegrown_recommended && <span style={{ color: C.gold }}>★</span>}
            </div>
            <p className="text-sm mb-3 line-clamp-2" style={{ color: C.textMuted }}>{v.description}</p>
            <div className="flex items-center justify-between text-xs" style={{ color: C.textSoft }}><span>{v.days_to_maturity} days</span><span>{v.difficulty_level}</span>{v.homegrown_rating && <span className="flex items-center gap-1"><span style={{ color: C.gold }}>★</span> {v.homegrown_rating}</span>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
